/* eslint-disable react-hooks/rules-of-hooks */

import { Permission } from '@idearoom/types';
import { OnlyExistingKeys } from '../types/Repo';
import { useAppSelector } from '../hooks';
import { unknownGroup } from '../constants/Group';
import { useGetPaymentsQuery } from '../services/leadApi';
import { isIdeaRoomGroup } from '../utils/userUtils';
import { PaymentOrder } from '../types/PaymentOrder';
import { MUIDataGridKey } from '../constants/MUIDataGrid';
import { hasAdminPermissions } from '../utils/permissionUtils';
import { AppState } from '../types/AppState';
import { unknownUser } from '../types/User';
import { getPaymentHeaderRequest } from '../utils/apiUtils';

const EMPTY_PAYMENTS_REFERENCE: Readonly<PaymentOrder[]> = [];

type UsePaymentRepoReturn<UsePaymentsType> = OnlyExistingKeys<{
  payments: UsePaymentsType extends true ? PaymentOrder[] : never;
  isLoadingPayments: UsePaymentsType extends true ? boolean : never;
}>;

export const usePaymentRepo = <UsePaymentsType extends boolean = false>({
  usePayments,
}: {
  usePayments?: UsePaymentsType;
} = {}): UsePaymentRepoReturn<UsePaymentsType> => {
  const { groupId = '', configurators: groupConfigurators = [] } = useAppSelector(
    (state: AppState) => state.currentUser.group || unknownGroup,
  );
  const currentUser = useAppSelector((state: AppState) => state.currentUser.user || unknownUser);
  const { systemGroupOrderConfigurators = [] } = useAppSelector(
    (state: AppState) => state.systemGroupOrderConfigurators,
  );
  const { dateRangeFilter: { startDate = undefined, endDate = undefined } = {} } = useAppSelector(
    (state) => state.muiDataGrid[MUIDataGridKey.Payments] || state.muiDataGrid[MUIDataGridKey.Default],
  );
  const dealers = useAppSelector((state: AppState) => state.currentUser.availableDealers || []);

  const result: any = {};
  if (usePayments) {
    const configurators = (
      isIdeaRoomGroup(groupId)
        ? systemGroupOrderConfigurators
        : groupConfigurators.map((c) => ({ configurator: c.key, vendor: c.vendor }))
    )
      .map((c) => `${c.configurator}-${c.vendor}`)
      .join(',');

    const paymentRequest = getPaymentHeaderRequest(groupId, configurators, startDate, endDate);
    const skip = !configurators.length;
    const { data: { orders: payments = EMPTY_PAYMENTS_REFERENCE } = {}, isFetching: isLoadingPayments } =
      useGetPaymentsQuery(
        { paymentRequest },
        {
          skip,
          refetchOnMountOrArgChange: true,
        },
      );

    let filteredPayments = payments;
    // Get the current user to filter on permissions and dealer if necessary
    const currentUserDealers = currentUser.dealers
      ? currentUser.dealers.filter((d) => d !== null && d !== undefined)
      : [];

    // Filter payments for SALES users to be only the payments owned by them
    if (
      currentUser.permissions.length === 0 ||
      (currentUser.permissions.length === 1 && currentUser.permissions[0] === Permission.Sales)
    ) {
      filteredPayments = filteredPayments.filter((payment) => {
        const orderOwner = payment.ownerEmail ? payment.ownerEmail : payment.salesRepEmail;
        return orderOwner ? orderOwner.toLowerCase() === currentUser.email.toLowerCase() : false;
      });
    } else if (currentUserDealers.length > 0 && !hasAdminPermissions(currentUser.permissions)) {
      const availableDealers = dealers.filter((dealer) => currentUserDealers.includes(dealer.id || 0));
      filteredPayments = filteredPayments.filter((payment) =>
        payment.orderDealerKey || payment.dealerKey
          ? availableDealers.some((dealer) => dealer.key === (payment.orderDealerKey || payment.dealerKey))
          : false,
      );
    } else if (
      currentUser.permissions.includes(Permission.Manager) &&
      !hasAdminPermissions(currentUser.permissions) &&
      !currentUser.permissions.includes(Permission.SuperUser)
    ) {
      // If a manager does not have any dealers assigned, only show them unassigned payments
      filteredPayments = filteredPayments.filter((payment) => !(payment.orderDealerKey || payment.dealerKey));
    }

    result.payments = filteredPayments;
    result.isLoadingPayments = isLoadingPayments;
  }

  return result;
};
